import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Network,
  Layers,
  Building2,
  CheckCircle2,
  ArrowRight,
  BookOpen,
  Mail,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const services = [
  {
    Icon: ShieldCheck,
    title: "IEC 62443 Reifegrad-Assessment",
    text: "Standortbestimmung Ihrer Gebäudeautomation nach IEC 62443-2-1 und -3-3: Wo stehen Organisation, Prozesse und Technik heute, und welche Security Levels sind für Ihre Liegenschaft realistisch?",
  },
  {
    Icon: Layers,
    title: "Zonen- und Conduit-Konzept",
    text: "Segmentierung Ihrer GA-Anlagen in Zonen und Conduits nach IEC 62443-3-2, mit Risikobetrachtung und ableitbaren Target Security Levels je Anlagenteil.",
  },
  {
    Icon: Network,
    title: "OT-Netzwerkarchitektur",
    text: "Von BACnet, KNX und Modbus bis zu Fernwartung und Cloud-Diensten: belastbare Netzwerkarchitektur, Härtung und sichere Fernzugriffe, konsequent an IEC 62443 ausgerichtet.",
  },
  {
    Icon: Building2,
    title: "Betriebsmodell OT-Security",
    text: "Rollen, Verantwortlichkeiten und Prozesse für den laufenden GA-Betrieb: Change, Patch, Incident und Lieferantensteuerung nach IEC 62443-2-4, verzahnt mit Facility und IT.",
  },
];

const benefits = [
  "Sie übersetzen IEC 62443 in konkrete Maßnahmen für Ihre GA, statt in abstrakte Normzitate.",
  "Sie schaffen die Basis, um NIS-2 und BSI INF.13 / INF.14 wirtschaftlich zu erfüllen.",
  "Sie reduzieren Ausfall- und Manipulationsrisiken im laufenden Gebäudebetrieb.",
  "Sie bekommen eine gemeinsame Sprache zwischen Betreiber, IT, OT, Planern und Integratoren.",
  "Sie priorisieren investitionsfähige Maßnahmen statt reiner Compliance-Deko.",
];

const faqs = [
  {
    q: "Was ist die IEC 62443?",
    a: "IEC 62443 ist die internationale Normenreihe für Cybersecurity in industriellen Automatisierungs- und Steuerungssystemen (IACS). Sie beschreibt Anforderungen an Betreiber, Integratoren und Hersteller, unter anderem zu Risikomanagement, Zonen und Conduits, Security Levels und Systemhärtung, und lässt sich sehr gut auf Gebäudeautomation übertragen.",
  },
  {
    q: "Warum ist IEC 62443 für Gebäudeautomation relevant?",
    a: "Moderne Gebäudeautomation ist ein OT-System: BACnet, KNX, Modbus, LON, MQTT und immer mehr IP-basierte Komponenten mit Fernzugriff. IEC 62443 liefert genau das Rahmenwerk, das für sicheren Betrieb, Segmentierung und Lieferantensteuerung fehlt, wenn man die GA nur wie klassische Gebäudetechnik behandelt.",
  },
  {
    q: "Wie hängen IEC 62443, NIS-2 und BSI Grundschutz zusammen?",
    a: "NIS-2 formuliert Pflichten auf hoher Ebene, BSI Infrastrukturbausteine INF.13 und INF.14 konkretisieren sie für Gebäude und Rechenzentren. IEC 62443 ergänzt die technische und organisatorische Detailebene für OT und GA. Ich mappe diese Ebenen so, dass eine Umsetzung mehrere Anforderungen gleichzeitig erschlägt.",
  },
  {
    q: "Was bedeuten Security Level 1 bis 4?",
    a: "IEC 62443 unterscheidet vier Security Levels, abhängig von der Motivation und den Ressourcen möglicher Angreifer, von zufälligen Fehlbedienungen (SL1) bis hin zu staatlichen Akteuren mit hohem Aufwand (SL4). Für die meisten kommerziellen Liegenschaften sind SL2 und ausgewählte Zonen auf SL3 ein realistisches Ziel.",
  },
  {
    q: "Wie starten wir konkret?",
    a: "Pragmatischer Einstieg ist ein IEC 62443 Reifegrad-Assessment für eine repräsentative Liegenschaft oder Anlage. Alternativ starten Sie mit dem FACHSPRECHPARTNER Workbook inklusive Self-Assessment und 60 Minuten Online Assessment und leiten daraus einen priorisierten Fahrplan ab.",
  },
  {
    q: "Brauche ich eine Zertifizierung nach IEC 62443?",
    a: "Für Betreiber steht in der Regel nicht das Zertifikat im Vordergrund, sondern ein tragfähiges Betriebsmodell. Zertifizierungen sind vor allem für Hersteller und Integratoren relevant. Wenn Sie diese Rolle einnehmen, klären wir Ziel, Scope und den passenden Zertifizierungspfad im Vorgespräch.",
  },
];

const Iec62443 = () => {
  const openContact = () =>
    window.dispatchEvent(new CustomEvent("open-contact-form"));

  const canonical = "https://fachsprechpartner.de/iec62443";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: "IEC 62443 Beratung für Gebäudeautomation",
        serviceType: "OT-Security nach IEC 62443",
        provider: {
          "@type": "Organization",
          name: "FACHSPRECHPARTNER",
          url: "https://fachsprechpartner.de",
        },
        areaServed: { "@type": "Country", name: "Deutschland" },
        description:
          "IEC 62443 in der Gebäudeautomation umsetzen: Reifegrad-Assessment, Zonen- und Conduit-Konzept, OT-Netzwerkarchitektur und Betriebsmodell für Betreiber, Planer und Integratoren.",
        url: canonical,
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fachsprechpartner.de/" },
          { "@type": "ListItem", position: 2, name: "IEC 62443", item: canonical },
        ],
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>IEC 62443 in der Gebäudeautomation: OT-Security Beratung</title>
        <meta
          name="description"
          content="IEC 62443 in der Gebäudeautomation umsetzen: Reifegrad-Assessment, Zonen- und Conduit-Konzept, OT-Netzwerkarchitektur und Betriebsmodell für sichere GA."
        />
        <meta
          name="keywords"
          content="IEC 62443, IEC62443, Gebäudeautomation, OT-Security, Zonen und Conduits, Security Level, BACnet Security, NIS-2"
        />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="de" href={canonical} />
        <link rel="alternate" hrefLang="x-default" href={canonical} />
        <meta property="og:title" content="IEC 62443 in der Gebäudeautomation: OT-Security Beratung" />
        <meta
          property="og:description"
          content="IEC 62443 in der Gebäudeautomation umsetzen: Reifegrad-Assessment, Zonen- und Conduit-Konzept, OT-Netzwerkarchitektur und Betriebsmodell."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="https://fachsprechpartner.de/og-image.webp" />
        <meta property="og:image:type" content="image/webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="FACHSPRECHPARTNER Tim Bauer, OT-Security und Gebäudeautomation" />
        <meta property="og:locale" content="de_DE" />
        <meta property="og:site_name" content="Fachsprechpartner" />
        <meta name="twitter:title" content="IEC 62443 in der Gebäudeautomation: OT-Security Beratung" />
        <meta name="twitter:description" content="IEC 62443 in der Gebäudeautomation umsetzen: Reifegrad-Assessment, Zonen- und Conduit-Konzept, OT-Netzwerkarchitektur und Betriebsmodell." />
        <meta name="twitter:image" content="https://fachsprechpartner.de/og-image.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Navbar />

      <main className="min-h-screen bg-background">
        {/* Hero */}
        <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-primary to-primary/90 text-primary-foreground">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 text-accent text-xs font-semibold uppercase tracking-wider mb-6">
                <ShieldCheck className="w-4 h-4" />
                IEC 62443 · Gebäudeautomation
              </div>
              <h1 className="text-4xl md:text-6xl font-heading font-bold leading-tight mb-6">
                IEC 62443 als Betriebsmodell für Ihre Gebäudeautomation.
              </h1>
              <p className="text-lg md:text-xl text-primary-foreground/85 mb-8 leading-relaxed max-w-3xl">
                Ich übersetze IEC 62443 in konkrete Maßnahmen für Betreiber,
                Planer und Integratoren von Gebäudeautomation, von OT-Organisation
                und Assetmanagement über Zonen und Conduits bis zu Endpoints und
                Fernwartung.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={openContact}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors"
                >
                  Assessment anfragen <ArrowRight className="w-4 h-4" />
                </button>
                <Link
                  to="/workbook"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 text-primary-foreground font-semibold hover:bg-primary-foreground/10 transition-colors"
                >
                  <BookOpen className="w-4 h-4" />
                  Zum Workbook
                </Link>
                <Link
                  to="/iec62443-goldstandard"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 text-primary-foreground font-semibold hover:bg-primary-foreground/10 transition-colors"
                >
                  <ShieldCheck className="w-4 h-4" />
                  Warum IEC 62443 Goldstandard ist
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Leistungen */}
        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Leistungen nach IEC 62443
            </h2>
            <p className="text-muted-foreground mb-12 max-w-2xl">
              Vier Bausteine, mit denen Sie IEC 62443 in der Gebäudeautomation
              wirksam umsetzen, ohne den Betrieb auszubremsen.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {services.map(({ Icon, title, text }) => (
                <div
                  key={title}
                  className="p-6 rounded-2xl border border-border bg-card hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-2">
                    {title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Nutzen */}
        <section className="py-16 md:py-24 bg-muted/40">
          <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Was Sie davon haben
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                IEC 62443 ist kein Selbstzweck. Sinn hat die Norm nur, wenn Sie
                daraus einen sicheren, prüfbaren und wirtschaftlichen Betrieb
                Ihrer Gebäudeautomation ableiten können.
              </p>
            </div>
            <ul className="space-y-3">
              {benefits.map((b) => (
                <li key={b} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                  <span className="text-foreground/90">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-8">
              Häufige Fragen zu IEC 62443
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`}>
                  <AccordionTrigger className="text-left font-semibold">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-primary text-primary-foreground">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              IEC 62443 in Ihre GA bringen
            </h2>
            <p className="text-primary-foreground/85 mb-8 leading-relaxed">
              Starten Sie mit dem Workbook für einen strukturierten Selbst-Check
              oder buchen Sie direkt ein Sparring, um Ihren Fahrplan gemeinsam zu
              schärfen.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/workbook"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                Workbook ansehen
              </Link>
              <button
                onClick={openContact}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 font-semibold hover:bg-primary-foreground/10 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Sparring anfragen
              </button>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Iec62443;